import React, { useState, useRef, useEffect } from "react";
import TradingViewLogo from "../Images/TradingView.png";

interface TradingViewinfoSidebarProps {
  isDark?: boolean;
}

const TradingViewinfoSidebar: React.FC<TradingViewinfoSidebarProps> = ({ isDark }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    handleResize(); // Set initial state
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsClicked(false);
      }
    };

    if (isClicked) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isClicked]);

  // Original Desktop Styles (base)
  const baseSidebarStyle: React.CSSProperties = {
    position: "fixed",
    top: "10%",
    right: isOpen ? "0" : "-400px",
    width: "400px",
    height: "80vh",
    background: isDark
      ? "rgba(25, 25, 30, 0.85)"
      : "rgba(255, 255, 255, 0.95)",
    borderRadius: "16px",
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.4)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    color: isDark ? "white" : "#000",
    padding: "25px",
    boxSizing: "border-box",
    transition: "right 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out, top 0.3s ease-in-out, padding 0.3s ease-in-out",
    overflowY: "auto",
    zIndex: 10000,
  };

  const baseTitleStyle: React.CSSProperties = {
    background: "linear-gradient(120deg, #4A9D6F 0%, #9259DE 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontSize: "32px",
    fontWeight: "bold",
    marginBottom: "10px",
  };

  const baseIconContainerStyle: React.CSSProperties = {
    width: "80px",
    height: "80px",
    background: isDark
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(0, 0, 0, 0.04)",
    borderRadius: "50%",
    margin: "0 auto 15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 0 20px rgba(61, 111, 182, 0.15)",
  };

  const baseLogoStyle: React.CSSProperties = {
    width: "52px",
    height: "52px",
    objectFit: "contain",
  };

  const baseDescriptionStyle: React.CSSProperties = {
    fontSize: "14px",
    opacity: "0.8",
    marginBottom: "15px",
  };

  const baseTextContainerStyle: React.CSSProperties = {
    textAlign: "center", 
    marginBottom: "20px"
  };

  const baseSectionStyle: React.CSSProperties = { marginBottom: "25px" };

  const baseSectionHeadingStyle: React.CSSProperties = {
    borderBottom: isDark
      ? "1px solid rgba(255,255,255,0.15)"
      : "1px solid rgba(0,0,0,0.1)",
    paddingBottom: "8px",
    marginBottom: "12px",
    fontSize: "15px",
    fontWeight: 600,
    color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)",
  };

  const baseListStyle: React.CSSProperties = {
    paddingLeft: "20px",
    fontSize: "14px",
    lineHeight: "1.5",
  };

  const baseParagraphStyle: React.CSSProperties = {
    fontSize: "14px",
    lineHeight: "1.5",
  };

  const baseTechSpecGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    fontSize: "14px",
  };

  const baseTechSpecItemStyle: React.CSSProperties = {
    background: isDark
      ? "rgba(255,255,255,0.06)"
      : "rgba(0,0,0,0.03)",
    padding: "10px",
    borderRadius: "8px",
    textAlign: "center",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
  };

  const baseFooterTextStyle: React.CSSProperties = {
    fontSize: "12px",
    opacity: "0.6",
    textAlign: "center",
    marginTop: "20px",
  };

  // Mobile-specific overrides
  const mobileSidebarStyle: React.CSSProperties = isMobileView ? {
    width: "90vw",
    height: "85vh",
    top: "7.5%",
    padding: "15px",
    right: isOpen ? "5vw" : "-95vw",
  } : {};

  const mobileTitleStyle: React.CSSProperties = isMobileView ? { fontSize: "24px", marginBottom: "8px" } : {};
  const mobileIconContainerStyle: React.CSSProperties = isMobileView ? { width: "60px", height: "60px", margin: "0 auto 10px" } : {};
  const mobileLogoStyle: React.CSSProperties = isMobileView ? { width: "38px", height: "38px" } : {};
  const mobileDescriptionStyle: React.CSSProperties = isMobileView ? { fontSize: "12px", marginBottom: "10px" } : {};
  const mobileTextContainerStyle: React.CSSProperties = isMobileView ? { marginBottom: "15px" } : {};
  const mobileSectionStyle: React.CSSProperties = isMobileView ? { marginBottom: "15px" } : {};
  const mobileSectionHeadingStyle: React.CSSProperties = isMobileView ? { fontSize: "14px", paddingBottom: "6px" } : {};
  const mobileListStyle: React.CSSProperties = isMobileView ? { paddingLeft: "15px", fontSize: "12px", lineHeight: "1.4" } : {};
  const mobileParagraphStyle: React.CSSProperties = isMobileView ? { fontSize: "12px", lineHeight: "1.4" } : {};
  const mobileTechSpecGridStyle: React.CSSProperties = isMobileView ? { gridTemplateColumns: "1fr", gap: "8px", fontSize: "12px" } : {};
  const mobileTechSpecItemStyle: React.CSSProperties = isMobileView ? { padding: "8px" } : {};
  const mobileFooterTextStyle: React.CSSProperties = isMobileView ? { fontSize: "10px", marginTop: "15px" } : {};

  // Merged styles
  const finalSidebarStyle = { ...baseSidebarStyle, ...mobileSidebarStyle };
  if (isMobileView && !isOpen) {
    finalSidebarStyle.right = "-95vw"; 
  } else if (!isMobileView && !isOpen) {
    finalSidebarStyle.right = "-400px";
  } else if (isMobileView && isOpen) {
    finalSidebarStyle.right = "5vw";
  } else {
    finalSidebarStyle.right = "0";
  }

  const finalTitleStyle = { ...baseTitleStyle, ...mobileTitleStyle };
  const finalIconContainerStyle = { ...baseIconContainerStyle, ...mobileIconContainerStyle };
  const finalLogoStyle = { ...baseLogoStyle, ...mobileLogoStyle };
  const finalDescriptionStyle = { ...baseDescriptionStyle, ...mobileDescriptionStyle };
  const finalTextContainerStyle = { ...baseTextContainerStyle, ...mobileTextContainerStyle };
  const finalSectionStyle = { ...baseSectionStyle, ...mobileSectionStyle };
  const finalSectionHeadingStyle = { ...baseSectionHeadingStyle, ...mobileSectionHeadingStyle };
  const finalListStyle = { ...baseListStyle, ...mobileListStyle };
  const finalParagraphStyle = { ...baseParagraphStyle, ...mobileParagraphStyle };
  const finalTechSpecGridStyle = { ...baseTechSpecGridStyle, ...mobileTechSpecGridStyle };
  const finalTechSpecItemStyle = { ...baseTechSpecItemStyle, ...mobileTechSpecItemStyle };
  const finalFooterTextStyle = { ...baseFooterTextStyle, ...mobileFooterTextStyle };

  return (
    <>
      {/* Info Button */}
      <div
        ref={buttonRef}
        onMouseEnter={() => !isClicked && setIsOpen(true)}
        onMouseLeave={() => !isClicked && setIsOpen(false)}
        onClick={() => {
          setIsClicked(!isClicked);
          setIsOpen(!isClicked);
        }}
        style={{
          color: isDark ? "#ffffff" : "#000000",
          fontSize: "20px",
          padding: "5px",
          cursor: "pointer",
          transition: "opacity 0.3s",
          display: "inline-flex",
          alignItems: "center",
          zIndex: 10001,
        }}
      >
        <img
          src={TradingViewLogo}
          alt="Info"
          style={{ width: "36px", height: "36px", objectFit: "contain" }}
        />
      </div>

      {/* Enhanced Sidebar */}
      <div
        ref={sidebarRef}
        style={finalSidebarStyle}
        onMouseEnter={() => !isClicked && setIsOpen(true)}
        onMouseLeave={() => !isClicked && setIsOpen(false)}
      >
        <div style={finalTextContainerStyle}>
          <div style={finalTitleStyle}>
            StockBot on Groq
          </div>
          <div style={finalIconContainerStyle}>
            <img
              src={TradingViewLogo}
              alt="TradingView Logo"
              style={finalLogoStyle}
            />
          </div>
          <div style={finalDescriptionStyle}>
            Real-Time Market Data via Groq Native Tool Calling
          </div>
        </div>

        <div style={finalSectionStyle}>
          <h3 style={finalSectionHeadingStyle}>
            Available Visualizations
          </h3>
          <p style={{...finalParagraphStyle, marginBottom: "10px"}}>
            StockBot can dynamically render 15 different interactive widgets:
          </p>
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px",
            fontSize: isMobileView ? "12px" : "13px",
            opacity: "0.9"
          }}>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Price & Overview
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Advanced Chart
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Financial Data
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Stock Screener
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Latest News
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Top Stories
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Market Overview
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Market Heatmap
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> ETF Heatmap
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Trending Stocks
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Technical Analysis
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Company Profile
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Symbol Info
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Forex Cross Rates
            </div>
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
              <span style={{color: "#4A9D6F"}}>•</span> Economic Calendar
            </div>
          </div>
        </div>

        <div style={finalSectionStyle}>
          <h3 style={finalSectionHeadingStyle}>
            Model Details
          </h3>
          <div style={finalTechSpecGridStyle}>
            <div style={finalTechSpecItemStyle}>
              <div style={{ fontWeight: 600, marginBottom: "2px" }}>Model</div>
              <div style={{ opacity: 0.8 }}>llama-3.1-8b-instant</div>
            </div>
            <div style={finalTechSpecItemStyle}>
              <div style={{ fontWeight: 600, marginBottom: "2px" }}>Inference</div>
              <div style={{ opacity: 0.8 }}>Groq LPU</div>
            </div>
            <div style={finalTechSpecItemStyle}>
              <div style={{ fontWeight: 600, marginBottom: "2px" }}>Data Source</div>
              <div style={{ opacity: 0.8 }}>TradingView</div>
            </div>
            <div style={finalTechSpecItemStyle}>
              <div style={{ fontWeight: 600, marginBottom: "2px" }}>Routing</div>
              <div style={{ opacity: 0.8 }}>Native Tool Calling</div>
            </div>
          </div>
        </div>

        <div style={finalSectionStyle}>
          <h3 style={finalSectionHeadingStyle}>
            Usage Notes
          </h3>
          <p style={finalParagraphStyle}>
            StockBot uses Groq's native tool calling to route your natural language
            requests to the appropriate TradingView widget. Ask for specific tickers,
            general market overviews, or comparative charts to instantly render
            interactive financial data.
          </p>
        </div>

        <div style={finalFooterTextStyle}>
          Powered by Groq + TradingView
        </div>
      </div>
    </>
  );
};

export default TradingViewinfoSidebar;